import React from "react";
import { Link } from "react-router-dom";
import { Badge, Card, CarIcon, HomeIcon, UmbrellaIcon } from "../ui";
import { daysUntil, formatDate, renewalVariant } from "../data/format";

/** Line-of-business badge (§2.7): icon + label, never a colored cell. */
export function LobBadge({ lobKey, lob, count }) {
  const icons = { auto: CarIcon, home: HomeIcon, umbrella: UmbrellaIcon };
  const Icon = icons[lobKey];
  return (
    <Badge variant="neutral">
      {Icon ? <Icon size={12} /> : null}
      {lob}
      {count > 1 ? ` ×${count}` : ""}
    </Badge>
  );
}

/** Renewal date + urgency badge; estimates are labeled, never silent. */
export function RenewalBadge({ renewal }) {
  if (!renewal) {
    return <span className="fk-muted">—</span>;
  }
  const days = daysUntil(renewal.date);
  return (
    <span className="feat-renewal">
      {formatDate(renewal.date)}
      {days != null && days >= 0 ? (
        <Badge variant={renewalVariant(days)}>
          {days === 0 ? "today" : `${days}d`}
          {renewal.estimated ? " · est." : ""}
        </Badge>
      ) : renewal.estimated ? (
        <Badge variant="neutral">est.</Badge>
      ) : null}
    </span>
  );
}

export function PolicyStatusBadge({ policy }) {
  return policy.policyActive === false ? (
    <Badge variant="neutral">Inactive</Badge>
  ) : (
    <Badge variant="success" dot>
      Active
    </Badge>
  );
}

/**
 * Display stage for a legacy quote record, derived from the three booleans
 * (sold → Won, presented → Presented, quoted → Quoted, else New). Display
 * only — Session H's pipelineStage replaces this in Phase 2.5.
 */
export function quoteStage(quote) {
  if (quote.sold) {
    return { label: "Won", variant: "success" };
  }
  if (quote.presented) {
    return { label: "Presented", variant: "warning" };
  }
  if (quote.quoted) {
    return { label: "Quoted", variant: "info" };
  }
  return { label: "New", variant: "neutral" };
}

export function QuoteStageBadge({ quote }) {
  const stage = quoteStage(quote);
  return <Badge variant={stage.variant}>{stage.label}</Badge>;
}

/**
 * Prospect pipeline stages (ROLE-DESIGN-2026-07-11 Part 3), ordered. The
 * enum strings are the spec's snake_case stage names (Session H owns the
 * authoritative values; flagged in the contract thread). `variant` is the
 * badge tone; referral surfaces keep Lost neutral (E: no red except errors).
 */
export const PIPELINE_STAGES = [
  { key: "new_referral", label: "New referral", shortLabel: "New", variant: "brand" },
  {
    key: "gathering_information",
    label: "Gathering information",
    shortLabel: "Gathering",
    variant: "neutral"
  },
  { key: "quoting", label: "Quoting", shortLabel: "Quoting", variant: "info" },
  { key: "quotes_completed", label: "Quotes completed", shortLabel: "Quoted", variant: "info" },
  {
    key: "quotes_presented",
    label: "Quotes presented",
    shortLabel: "Presented",
    variant: "warning"
  },
  {
    key: "applications_sent",
    label: "Applications sent",
    shortLabel: "Apps sent",
    variant: "info"
  },
  {
    key: "applications_signed",
    label: "Applications signed",
    shortLabel: "Apps signed",
    variant: "info"
  },
  { key: "final_review", label: "Final review", shortLabel: "Final review", variant: "warning" },
  { key: "submitted", label: "Submitted", shortLabel: "Submitted", variant: "info" },
  { key: "bound", label: "Bound", shortLabel: "Bound", variant: "success", terminal: true },
  { key: "lost", label: "Lost", shortLabel: "Lost", variant: "neutral", terminal: true }
];

export function prospectStageInfo(stageKey) {
  return (
    PIPELINE_STAGES.find((s) => s.key === (stageKey || "new_referral")) || {
      key: stageKey,
      label: stageKey,
      shortLabel: stageKey,
      variant: "neutral"
    }
  );
}

/**
 * Retention cases (ROLE-DESIGN Part 10) ride the Prospect record additively:
 * caseType:'retention' marks a client-initiated re-quote, clientRef links the
 * existing Client. Both helpers tolerate a populated ref or a bare id, and an
 * absent caseType (legacy prospects) reads as a normal referral.
 */
export function isRetentionCase(prospect) {
  return Boolean(prospect && prospect.caseType === "retention");
}

export function caseClientRef(prospect) {
  const ref = prospect && prospect.clientRef;
  return ref ? String(ref._id || ref) : null;
}

/**
 * The quotes on a case (Coordinator ruling B, D6): sourced CLIENT-keyed —
 * quotes whose convertedToClientID matches the case's linked client. Built
 * case-type-agnostic so a later prospect→quote linkage lights up the
 * unconverted-referral path by extending only this helper.
 *
 * ⚠️ v1 imprecision (accepted): this surfaces ALL of that client's quotes on
 * the case, not per-case attribution — per-case arrives with the post-gate
 * prospect→quote linkage. An unconverted referral prospect has no clientRef →
 * [] → no chip (E's chip law: no quotes, no affordance).
 */
export function quotesForCase(prospect, quotes) {
  const clientId = caseClientRef(prospect);
  if (!clientId) {
    return [];
  }
  return (quotes || []).filter(
    (q) => q.convertedToClientID && String(q.convertedToClientID) === clientId
  );
}

const TERMINAL = ["bound", "lost"];

/** A household's in-flight retention cases (caseType:'retention', clientRef
    matches, not yet bound/lost). Absent-not-empty: returns [] for none. */
export function openCasesForClient(prospects, clientId) {
  const id = String(clientId);
  return (prospects || []).filter(
    (p) =>
      isRetentionCase(p) &&
      caseClientRef(p) === id &&
      !TERMINAL.includes(p.pipelineStage || "new_referral")
  );
}

/** Hub "open cases" strip (Phase 3, §10.6). Each row deep-links to the
    prospect's pipeline row. Absent (renders nothing) when there are none —
    never an empty card (E: absent, not empty). */
export function OpenCasesCard({ cases }) {
  if (!cases || cases.length === 0) {
    return null;
  }
  return (
    <Card title={`Open cases (${cases.length})`}>
      <div className="feat-feed">
        {cases.map((c) => {
          const info = prospectStageInfo(c.pipelineStage);
          return (
            <Link
              key={c._id}
              to={`/app/pipeline?prospect=${c._id}`}
              className="feat-feed__row"
              style={{ textDecoration: "none" }}
            >
              <Badge variant="info">Retention</Badge>
              <span className="feat-feed__main">
                <span className="feat-feed__title">{info.label}</span>
                <span className="feat-feed__meta">
                  {[c.lines, c.renewalDate ? `renews ${formatDate(c.renewalDate)}` : null]
                    .filter(Boolean)
                    .join(" · ") || "In progress"}
                </span>
              </span>
            </Link>
          );
        })}
      </div>
    </Card>
  );
}
